import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import { sideBarItems } from "../../utils";
import { Pagination } from "../../components/common/pagination";
import { toast } from "react-hot-toast";
import RideShareAdminService from "../../services/admin/RideShareAdminService";
import { RIDE_OVERRIDE_KEY } from "../../config";

// Secret status-override tools. Rendered only when the build carries
// REACT_APP_RIDE_OVERRIDE_KEY — other admins never see these controls.
const OVERRIDE_ENABLED = Boolean(RIDE_OVERRIDE_KEY);

const VEHICLE_TYPES = [
  "bike",
  "cng",
  "sedan_standard",
  "sedan_premium",
  "sedan_luxury",
  "microbus_7",
  "microbus_10",
  "microbus_15",
  "microbus_28",
];

const RIDE_STATUSES = [
  "searching",
  "driver_assigned",
  "driver_arriving",
  "in_progress",
  "completed",
  "cancelled",
];

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) return "BDT 0.00";
  return `BDT ${amount.toFixed(2)}`;
};

function RideShareCompleted() {
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
    vehicleType: "",
  });

  const [selectedRideId, setSelectedRideId] = useState("");
  const [selectedRideDetail, setSelectedRideDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [overrideSelection, setOverrideSelection] = useState([]);
  const [overrideBusy, setOverrideBusy] = useState(false);

  const getError = (err) => err?.response?.data?.error || "Operation failed";

  const queryParams = useMemo(() => {
    const params = {
      page,
      limit,
    };

    if (filters.search.trim()) {
      params.search = filters.search.trim();
    }
    if (filters.status) {
      params.status = filters.status;
    }
    if (filters.fromDate) {
      params.fromDate = filters.fromDate;
    }
    if (filters.toDate) {
      params.toDate = filters.toDate;
    }
    if (filters.vehicleType) {
      params.vehicleType = filters.vehicleType;
    }
    return params;
  }, [filters, page, limit]);

  const loadRideShareRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await RideShareAdminService.getCompletedRides(queryParams);
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const pagination = res?.data?.pagination || {};
      setRides(data);
      setTotal(Number(pagination.total || 0));
      setTotalPages(Number(pagination.totalPages || 1));
    } catch (err) {
      toast.error(getError(err));
      setRides([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  const loadRideDetail = async (rideId) => {
    if (!rideId) return;
    setDetailLoading(true);
    try {
      const res = await RideShareAdminService.getCompletedRideDetails(rideId);
      setSelectedRideDetail(res?.data?.data || null);
      setSelectedRideId(rideId);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleOverrideSelection = (rideId) => {
    setOverrideSelection((prev) =>
      prev.includes(rideId)
        ? prev.filter((id) => id !== rideId)
        : [...prev, rideId],
    );
  };

  const handleForceCompleteSelected = async () => {
    if (!overrideSelection.length || overrideBusy) return;
    if (
      !window.confirm(
        `Mark ${overrideSelection.length} cancelled ride(s) as completed? This is undoable.`,
      )
    ) {
      return;
    }
    setOverrideBusy(true);
    let ok = 0;
    let failed = 0;
    for (const rideId of overrideSelection) {
      try {
        await RideShareAdminService.forceCompleteRide(rideId);
        ok += 1;
      } catch (err) {
        failed += 1;
        toast.error(`${rideId}: ${getError(err)}`);
      }
    }
    setOverrideBusy(false);
    setOverrideSelection([]);
    if (ok) toast.success(`${ok} ride(s) marked completed`);
    if (failed) toast.error(`${failed} ride(s) failed`);
    loadRideShareRequests();
  };

  const handleUndoOverride = async (rideId) => {
    if (overrideBusy) return;
    if (!window.confirm("Undo override and restore this ride to cancelled?")) {
      return;
    }
    setOverrideBusy(true);
    try {
      await RideShareAdminService.undoForceCompleteRide(rideId);
      toast.success("Ride restored to previous state");
      loadRideShareRequests();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setOverrideBusy(false);
    }
  };

  useEffect(() => {
    loadRideShareRequests();
  }, [loadRideShareRequests]);

  useEffect(() => {
    setOverrideSelection([]);
  }, [queryParams]);

  useEffect(() => {
    setPage(1);
  }, [
    filters.search,
    filters.status,
    filters.fromDate,
    filters.toDate,
    filters.vehicleType,
  ]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const nextPage = () =>
    setPage((prev) => (prev < totalPages ? prev + 1 : prev));
  const prevPage = () => setPage((prev) => (prev > 1 ? prev - 1 : prev));

  return (
    <Layout title={sideBarItems.rideShareCompleted.key}>
      <div className="w-full py-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-5">
          Ride Share Requests
        </h1>

        <div className="bg-white shadow-md rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <input
              className="px-3 py-2 border rounded-md"
              placeholder="Search ride ID / pickup / dropoff"
              value={filters.search}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, search: e.target.value }))
              }
            />
            <select
              className="px-3 py-2 border rounded-md"
              value={filters.status}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <option value="">All Statuses</option>
              {RIDE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <input
              className="px-3 py-2 border rounded-md"
              type="date"
              value={filters.fromDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
              }
            />
            <input
              className="px-3 py-2 border rounded-md"
              type="date"
              value={filters.toDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, toDate: e.target.value }))
              }
            />
            <select
              className="px-3 py-2 border rounded-md"
              value={filters.vehicleType}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, vehicleType: e.target.value }))
              }
            >
              <option value="">All Vehicle Types</option>
              {VEHICLE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              onClick={() =>
                setFilters({
                  search: "",
                  status: "",
                  fromDate: "",
                  toDate: "",
                  vehicleType: "",
                })
              }
            >
              Reset Filters
            </button>
            <button
              className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              onClick={loadRideShareRequests}
            >
              Refresh
            </button>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Total ride-share requests:{" "}
              <span className="font-semibold text-gray-900">{total}</span>
            </div>
            {OVERRIDE_ENABLED && overrideSelection.length > 0 && (
              <button
                className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                disabled={overrideBusy}
                onClick={handleForceCompleteSelected}
              >
                {overrideBusy
                  ? "Working..."
                  : `Mark ${overrideSelection.length} as Completed`}
              </button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-600">
                  {OVERRIDE_ENABLED && <th className="px-3 py-2 border" />}
                  <th className="px-3 py-2 border">Ride</th>
                  <th className="px-3 py-2 border">Notified</th>
                  <th className="px-3 py-2 border">Status</th>
                  <th className="px-3 py-2 border">Requested At</th>
                  <th className="px-3 py-2 border">Passenger</th>
                  <th className="px-3 py-2 border">Driver</th>
                  <th className="px-3 py-2 border">Pickup → Dropoff</th>
                  <th className="px-3 py-2 border">Estimated Price</th>
                  <th className="px-3 py-2 border">Accepted Bid</th>
                  <th className="px-3 py-2 border">Action</th>
                </tr>
              </thead>
              <tbody>
                {rides.map((ride) => (
                  <tr
                    key={ride.rideId}
                    className={`text-sm align-top ${
                      ride.status === "completed" ? "bg-green-100" : ""
                    }`}
                  >
                    {OVERRIDE_ENABLED && (
                      <td className="px-3 py-2 border text-center">
                        {ride.overrideEligible && (
                          <input
                            type="checkbox"
                            checked={overrideSelection.includes(ride.rideId)}
                            onChange={() =>
                              toggleOverrideSelection(ride.rideId)
                            }
                          />
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 border" title={ride.rideId}>
                      {ride.rideId}
                    </td>
                    <td className="px-3 py-2 border text-center">
                      {Number(ride.notifiedDriversCount || 0)}
                    </td>
                    <td className="px-3 py-2 border">
                      <span className="capitalize">{ride.status || "N/A"}</span>
                      {OVERRIDE_ENABLED && ride?.manualOverride?.active && (
                        <div className="text-xs text-orange-600">overridden</div>
                      )}
                    </td>
                    <td className="px-3 py-2 border">
                      {formatDateTime(ride.requestedAt)}
                    </td>
                    <td className="px-3 py-2 border">
                      <div className="font-medium">
                        {ride?.passenger?.name || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {ride?.passenger?.mobile || "N/A"}
                      </div>
                    </td>
                    <td className="px-3 py-2 border">
                      <div className="font-medium">
                        {ride?.driver?.name || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {ride?.driver?.mobile || "N/A"}
                      </div>
                    </td>
                    <td className="px-3 py-2 border">
                      <div
                        className="max-w-xs truncate"
                        title={ride?.pickupLocation?.address || ""}
                      >
                        {ride?.pickupLocation?.address || "N/A"}
                      </div>
                      <div
                        className="max-w-xs truncate text-xs text-gray-500"
                        title={ride?.dropoffLocation?.address || ""}
                      >
                        {ride?.dropoffLocation?.address || "N/A"}
                      </div>
                    </td>
                    <td className="px-3 py-2 border">
                      {formatMoney(ride.estimatedFare)}
                    </td>
                    <td className="px-3 py-2 border">
                      {formatMoney(ride.acceptedBidAmount)}
                    </td>
                    <td className="px-3 py-2 border">
                      <button
                        className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
                        onClick={() => loadRideDetail(ride.rideId)}
                      >
                        View Full
                      </button>
                      {OVERRIDE_ENABLED && ride?.manualOverride?.active && (
                        <button
                          className="mt-1 block px-3 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50"
                          disabled={overrideBusy}
                          onClick={() => handleUndoOverride(ride.rideId)}
                        >
                          Undo
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && rides.length === 0 && (
                  <tr>
                    <td
                      colSpan={OVERRIDE_ENABLED ? 11 : 10}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      No ride-share requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {loading && (
            <p className="text-sm text-gray-500 mt-4">Loading rides...</p>
          )}

          {total > 0 && (
            <div className="flex justify-center items-center mt-6">
              <Pagination
                page={page}
                totalPages={totalPages}
                setPage={setPage}
                limit={limit}
                setLimit={setLimit}
                prevPage={prevPage}
                nextPage={nextPage}
              />
            </div>
          )}
        </div>
      </div>

      {selectedRideId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70] flex items-center justify-center z-50">
          <div
            className="relative mx-auto p-6 border w-full max-w-5xl shadow-2xl rounded-lg bg-white flex flex-col"
            style={{ maxHeight: "80vh" }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">
                Ride Full Details
              </h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => {
                  setSelectedRideId("");
                  setSelectedRideDetail(null);
                }}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            {detailLoading && (
              <p className="text-sm text-gray-500">Loading ride detail...</p>
            )}

            {!detailLoading && selectedRideDetail && (
              <div className="space-y-4 overflow-y-auto pr-1 flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">Ride ID</div>
                    <div className="font-semibold break-all">
                      {selectedRideDetail.rideId}
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">Status</div>
                    <div className="font-semibold capitalize">
                      {selectedRideDetail.status || "N/A"}
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">Vehicle Model</div>
                    <div className="font-semibold">
                      {selectedRideDetail?.driver?.vehicleModel ||
                        selectedRideDetail.vehicleType ||
                        "N/A"}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="p-3 border rounded">
                    <h4 className="font-semibold mb-2">Passenger</h4>
                    <p>Name: {selectedRideDetail?.passenger?.name || "N/A"}</p>
                    <p>
                      Mobile: {selectedRideDetail?.passenger?.mobile || "N/A"}
                    </p>
                  </div>
                  <div className="p-3 border rounded">
                    <h4 className="font-semibold mb-2">Driver</h4>
                    <p>Name: {selectedRideDetail?.driver?.name || "N/A"}</p>
                    <p>Mobile: {selectedRideDetail?.driver?.mobile || "N/A"}</p>
                    <p>
                      Number Plate:{" "}
                      {selectedRideDetail?.driver?.numberPlate ||
                        selectedRideDetail?.driver?.vehicleNo ||
                        "N/A"}
                    </p>
                  </div>
                </div>

                <div className="p-3 border rounded text-sm">
                  <h4 className="font-semibold mb-2">Locations</h4>
                  <p>
                    <span className="font-medium">Pickup:</span>{" "}
                    {selectedRideDetail?.pickupLocation?.address || "N/A"}
                  </p>
                  <p>
                    <span className="font-medium">Dropoff:</span>{" "}
                    {selectedRideDetail?.dropoffLocation?.address || "N/A"}
                  </p>
                  <p>
                    <span className="font-medium">Stops:</span>{" "}
                    {Array.isArray(selectedRideDetail?.stops) &&
                    selectedRideDetail.stops.length
                      ? selectedRideDetail.stops
                          .map((stop) => stop?.address || "N/A")
                          .join(" | ")
                      : "None"}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">Estimated Price</div>
                    <div className="font-semibold">
                      {formatMoney(selectedRideDetail.estimatedFare)}
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">
                      Driver Accepted Bid
                    </div>
                    <div className="font-semibold">
                      {formatMoney(selectedRideDetail.acceptedBidAmount)}
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded">
                    <div className="text-xs text-gray-500">
                      Distance / Duration
                    </div>
                    <div className="font-semibold">
                      {Number(selectedRideDetail.actualDistance || 0)} km /{" "}
                      {Number(selectedRideDetail.actualDuration || 0)} min
                    </div>
                  </div>
                </div>

                <div className="p-3 border rounded text-sm">
                  <h4 className="font-semibold mb-2">Timeline</h4>
                  <p>
                    Requested:{" "}
                    {formatDateTime(selectedRideDetail?.timeline?.requestedAt)}
                  </p>
                  <p>
                    Accepted:{" "}
                    {formatDateTime(selectedRideDetail?.timeline?.acceptedAt)}
                  </p>
                  <p>
                    Started:{" "}
                    {formatDateTime(selectedRideDetail?.timeline?.startedAt)}
                  </p>
                  <p>
                    Completed:{" "}
                    {formatDateTime(selectedRideDetail?.timeline?.completedAt)}
                  </p>
                </div>

                <div className="p-3 border rounded text-sm">
                  <h4 className="font-semibold mb-2">
                    Notified Drivers (
                      {Number(selectedRideDetail?.notifiedDriversCount || 0)})
                    </h4>
                    {(selectedRideDetail?.notifiedDrivers || []).length ? (
                      <table className="min-w-full border border-gray-200">
                        <thead>
                          <tr className="bg-gray-50 text-left text-xs text-gray-600">
                            <th className="px-2 py-1 border">Name</th>
                            <th className="px-2 py-1 border">Mobile</th>
                            <th className="px-2 py-1 border">Vehicle</th>
                            <th className="px-2 py-1 border">Number Plate</th>
                            <th className="px-2 py-1 border">Type</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedRideDetail.notifiedDrivers.map((driver) => (
                            <tr key={driver.id} className="text-xs">
                              <td className="px-2 py-1 border">
                                {driver.name || "N/A"}
                              </td>
                              <td className="px-2 py-1 border">
                                {driver.mobile || "N/A"}
                              </td>
                              <td className="px-2 py-1 border">
                                {[driver.vehicleBrand, driver.vehicleModel]
                                  .filter(Boolean)
                                  .join(" ") || "N/A"}
                              </td>
                              <td className="px-2 py-1 border">
                                {driver.numberPlate || "N/A"}
                              </td>
                              <td className="px-2 py-1 border">
                                {driver.vehicleType || "N/A"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-gray-500">
                        No drivers were notified about this trip.
                      </p>
                    )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}

export default RideShareCompleted;
